import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle, CheckCircle2, Loader2, Lock, ShoppingBasket, User } from "lucide-react";

export const Route = createFileRoute("/invitation/$token")({
  component: InvitationPage,
  head: () => ({
    meta: [
      { title: "Activar invitación — Structura Market" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

interface InvitationData {
  businessName: string;
  email: string;
  trialDays: number;
  invitedBy: string;
  notes?: string | null;
  expiresAt: string;
}

function InvitationPage() {
  const { token } = Route.useParams();
  const navigate  = useNavigate();

  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [loadError, setLoadError]   = useState<string | null>(null);
  const [loading, setLoading]       = useState(true);

  const [ownerName, setOwnerName]           = useState("");
  const [password, setPassword]             = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [submitting, setSubmitting]         = useState(false);
  const [submitError, setSubmitError]       = useState<string | null>(null);
  const [done, setDone]                     = useState(false);
  const [loginUrl, setLoginUrl]             = useState("/acceso/comercio");

  useEffect(() => {
    fetch(`/api/invitations/${token}`)
      .then((r) => r.json())
      .then((d: InvitationData & { error?: string }) => {
        if (d.error) setLoadError(d.error);
        else setInvitation(d);
      })
      .catch(() => setLoadError("Error al cargar la invitación."))
      .finally(() => setLoading(false));
  }, [token]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password !== passwordConfirm) {
      setSubmitError("Las contraseñas no coinciden.");
      return;
    }
    if (password.length < 8) {
      setSubmitError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch(`/api/invitations/${token}`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ ownerName: ownerName.trim(), password }),
      });
      const data = (await res.json()) as { ok?: boolean; loginUrl?: string; error?: string };
      if (!res.ok || data.error) {
        setSubmitError(data.error ?? "Error al activar la cuenta.");
        return;
      }
      setLoginUrl(data.loginUrl ?? "/acceso/comercio");
      setDone(true);
    } catch {
      setSubmitError("Error de conexión. Intentá de nuevo.");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="size-8 text-primary animate-spin" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-md w-full rounded-2xl border border-destructive/30 bg-card p-8 text-center">
          <AlertCircle className="size-12 text-destructive mx-auto mb-4" />
          <h1 className="text-xl font-bold text-foreground mb-2">Invitación no disponible</h1>
          <p className="text-muted-foreground text-sm">{loadError}</p>
          <a
            href="/"
            className="mt-6 inline-block text-primary hover:text-primary/80 text-sm underline"
          >
            Volver al inicio
          </a>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-md w-full rounded-2xl border border-green-200 bg-card p-10 text-center dark:border-green-900/40">
          <CheckCircle2 className="size-14 text-primary mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-foreground mb-3">¡Tu cuenta está lista!</h1>
          <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
            Tu período de prueba de{" "}
            <strong className="text-foreground">{invitation?.trialDays} días</strong> ha comenzado.
            Ingresá con el email y contraseña que acabás de crear.
          </p>
          <a
            href={loginUrl}
            className="inline-block bg-primary hover:bg-primary/90 text-primary-foreground font-bold px-8 py-3 rounded-xl transition-colors"
          >
            Ingresar a Structura Market →
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-6">
            <ShoppingBasket className="size-7 text-primary" />
            <span className="text-2xl font-bold text-foreground">
              Structura<span className="text-primary">Market</span>
            </span>
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Activar invitación</h1>
          <p className="text-muted-foreground text-sm">
            {invitation?.invitedBy} te invita a probar Structura Market
          </p>
        </div>

        {/* Invitation card */}
        <div className="rounded-2xl border border-border bg-card p-6 mb-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <ShoppingBasket className="size-5 text-primary" />
            </div>
            <div>
              <p className="text-foreground font-semibold">{invitation?.businessName}</p>
              <p className="text-muted-foreground text-sm">{invitation?.email}</p>
            </div>
          </div>
          {invitation?.notes && (
            <p className="text-muted-foreground text-sm border-l-2 border-primary pl-3 mb-4 italic">
              "{invitation.notes}"
            </p>
          )}
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex items-center gap-3">
            <CheckCircle2 className="size-5 text-primary shrink-0" />
            <div>
              <p className="text-foreground text-sm font-semibold">
                {invitation?.trialDays} días de trial
              </p>
              <p className="text-muted-foreground text-xs">Acceso completo · Activación por invitación</p>
            </div>
          </div>
        </div>

        {/* Account setup form */}
        <form
          onSubmit={(e) => void handleSubmit(e)}
          className="rounded-2xl border border-border bg-card p-6 space-y-5"
        >
          <h2 className="text-foreground font-semibold mb-1">Configurá tu acceso</h2>

          <div className="space-y-1.5">
            <Label htmlFor="ownerName" className="text-sm">Tu nombre completo</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                id="ownerName"
                value={ownerName}
                onChange={(e) => setOwnerName(e.target.value)}
                placeholder="Nombre y apellido"
                required
                className="pl-9"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-sm">Contraseña</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mínimo 8 caracteres"
                required
                minLength={8}
                className="pl-9"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="passwordConfirm" className="text-sm">Confirmar contraseña</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                id="passwordConfirm"
                type="password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                placeholder="Repetí la contraseña"
                required
                className="pl-9"
              />
            </div>
          </div>

          {submitError && (
            <div className="flex items-center gap-2 text-destructive text-sm bg-destructive/5 border border-destructive/20 rounded-lg p-3">
              <AlertCircle className="size-4 shrink-0" />
              {submitError}
            </div>
          )}

          <Button type="submit" disabled={submitting} className="w-full font-semibold py-2.5">
            {submitting ? (
              <><Loader2 className="size-4 animate-spin mr-2" />Activando cuenta…</>
            ) : (
              "Activar cuenta →"
            )}
          </Button>

          <p className="text-xs text-muted-foreground text-center leading-relaxed">
            Al activar aceptás las condiciones del período de prueba:{" "}
            {invitation?.trialDays} días de trial según la invitación recibida.
          </p>
        </form>
      </div>
    </div>
  );
}
